<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { Inertia } from '@inertiajs/inertia';
import { Link, Head, useForm, usePage } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import BaseIcon from "@/Components/BaseIcon.vue";
import BaseButtons from "@/Components/BaseButtons.vue";
import BaseButton from "@/Components/BaseButton.vue";
import CardBoxModal from "@/Components/CardBoxModal.vue";
import BreezeLabel from '@/Components/Label.vue';
import BreezeInput from '@/Components/Input.vue';
import BreezeButton from '@/Components/Button.vue';
import ClassroomTopicManager from "@/Components/ClassroomTopicManager.vue";
import { QuillEditor } from '@vueup/vue-quill';
import axios from 'axios';
import {
    mdiEye,
    mdiTrashCan,
    mdiPencil,
    mdiCheckCircle,
    mdiCloseCircle,
    mdiBook,
    mdiBookOpenVariant,
    mdiDownload,
    mdiPaperclip,
    mdiSend,
    mdiRefresh,
    mdiInformationOutline,
    mdiAccountVoice,
    mdiContentSave,
    mdiPlus,
    mdiPlusCircle,
    mdiClockOutline,
    mdiFileDocumentOutline,
    mdiLinkVariant,
    mdiCalendarCheck,
    mdiBullhornOutline,
    mdiStarOutline,
    mdiFilePdfBox,
    mdiFileWordBox,
    mdiFileExcelBox,
    mdiArrowLeft
} from "@mdi/js";

const props = defineProps({
    titleup: String,
    object: {
        type: Object,
        default: () => ({ id: 0, label: 'Curso' })
    },
    guides: {
        type: Array,
        default: () => []
    },
    anuncios: {
        type: Array,
        default: () => []
    },
    temas: {
        type: Array,
        default: () => []
    },
    resources: {
        type: Array,
        default: () => []
    },
    resourceLinks: {
        type: Array,
        default: () => []
    },
    classroom_label: {
        type: String,
        default: ''
    },
    tasks: {
        type: Array,
        default: () => []
    },
    taskResponses: {
        type: Array,
        default: () => []
    },
    arrayCalifications: {
        type: Array,
        default: () => []
    },
    publishedBimestres: Object,
    publishCriteria: {
        type: [String, Array],
        default: 'examenes'
    },
    trans: {
        type: Object,
        default: () => ({})
    },
    back_link: String,
    nowDayLetter: {
        type: String,
        default: ''
    },
    nowMonthLetter: {
        type: String,
        default: ''
    },
    nowDateFormated: String,
    nowDay: {
        type: String,
        default: ''
    },
    bimestreActual: {
        type: String,
        default: ''
    },
    semanaActual: {
        type: [Number, String],
        default: 1
    },
    classroom: Object,
    studentId: Number,
    ciclos: Array,
    refCicloActual: String
});

// Control de Tabs
const openTab = ref(1);
const toggleTabs = (tabNumber) => {
    openTab.value = tabNumber;
};

const page = usePage();
const confGlobal = computed(() => page.props.value.confGlobal || {});

// Manejo dinámico de criterios a publicar
const activeCriteria = computed(() => {
    let raw = props.publishCriteria;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        if (raw === 'examenes') return ['examen_parcial', 'examen_parcial2', 'examen_final'];
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
        } catch (e) {
            if (raw.includes(',')) return raw.split(',').map(s => s.trim());
        }
    }
    return ['examen_parcial', 'examen_parcial2', 'examen_final'];
});

const evalCriteriaDefs = [
    { key: 'criterio1', defaultLabel: 'Criterio 1', short: 'C1' },
    { key: 'criterio2', defaultLabel: 'Criterio 2', short: 'C2' },
    { key: 'criterio3', defaultLabel: 'Criterio 3', short: 'C3' },
    { key: 'criterio4', defaultLabel: 'Criterio 4', short: 'C4' },
    { key: 'criterio5', defaultLabel: 'Criterio 5', short: 'C5' },
    { key: 'criterio6', defaultLabel: 'Criterio 6', short: 'C6' },
];

const visibleEvalCriteria = computed(() => {
    return evalCriteriaDefs
        .filter(item => activeCriteria.value.includes(item.key))
        .map(item => ({
            ...item,
            label: (props.trans && props.trans[item.key]) ? props.trans[item.key] : item.defaultLabel
        }));
});

const showExamenParcial = computed(() => activeCriteria.value.includes('examen_parcial'));
const showExamenParcial2 = computed(() => activeCriteria.value.includes('examen_parcial2'));
const showExamenFinal = computed(() => activeCriteria.value.includes('examen_final'));

// Mapeo reactivo optimizado O(1) de respuestas de tareas
const taskResponsesMap = computed(() => {
    const map = {};
    (props.taskResponses || []).forEach(tr => {
        if (tr && tr.id_task) {
            map[tr.id_task] = tr;
        }
    });
    return map;
});

// Contador de tareas pendientes para badge dinámico
const pendingTasksCount = computed(() => {
    return (props.tasks || []).filter(t => {
        const resp = taskResponsesMap.value[t.id];
        return !resp || !resp.status;
    }).length;
});

// Total de recursos disponibles
const totalResourcesCount = computed(() => {
    return (props.resources?.length || 0) + (props.resourceLinks?.length || 0) + (props.guides?.length || 0);
});

const hasExamenParcial2 = computed(() => {
    return (props.arrayCalifications || []).some(
        c => c.examen_parcial2 !== null && c.examen_parcial2 !== undefined && c.examen_parcial2 !== '' && c.examen_parcial2 !== 0
    );
});

// Post-it / Bloc de Notas
const nota_privada_label = ref(null);
const postit = ref({
    id: 0,
    description: ''
});
const alertPostit = ref('');
const isSavingPostit = ref(false);
const lastSavedDescription = ref('');

const getPostit = () => {
    const url = '/postit/object?type_object=student_course&fk_object=' + props.object.id + '&fk_object2=0';
    axios.get(url).then(function (response) {
        const res = response.data?.data || response.data || {};
        postit.value.id = res.id ? res.id : 0;
        postit.value.description = res.description || '';
        lastSavedDescription.value = postit.value.description;
    }).catch(function (error) {
        console.warn('Error cargando nota privada:', error);
    });
};

const updateOrCreatePostit = (id = 0, content = '') => {
    // Si no ha cambiado respecto al último guardado, evitar petición innecesaria
    if (content === lastSavedDescription.value && (id > 0 || !content)) {
        return;
    }

    isSavingPostit.value = true;
    const title = 'Bloc de Notas';
    if (id > 0) {
        axios.put(route('postit-actualizar'), {
            id: id,
            description: content
        }).then(function (response) {
            lastSavedDescription.value = content;
            showAlertPostit('¡Guardado!');
        }).catch(function (error) {
            console.error(error);
            if (error?.response?.status === 419) {
                if (confirm('Se requiere actualizar por tiempo de inactividad...')) {
                    window.location.href = 'login';
                }
            }
        }).finally(() => {
            isSavingPostit.value = false;
        });
    } else {
        axios.post(route('postit-registrar'), {
            type_object: 'student_course',
            fk_object: props.object.id,
            fk_object2: 0,
            label: title,
            description: content
        }).then(function (response) {
            const savedData = response.data?.data || {};
            if (savedData.id) {
                postit.value.id = savedData.id;
            }
            lastSavedDescription.value = content;
            showAlertPostit('¡Guardado!');
            getPostit();
        }).catch(function (error) {
            console.error(error);
        }).finally(() => {
            isSavingPostit.value = false;
        });
    }
};

const showAlertPostit = (text = '', time = 2) => {
    alertPostit.value = text;
    setTimeout(() => {
        alertPostit.value = '';
    }, time * 1000);
};

// Modales y formularios de tareas
const isModalActive = ref(false);
const isModalDangerActive = ref(false);
const isModalFullActive = ref(false);
const modalFullSrc = ref('');
const rowID = ref(0);
const rowParentID = ref(null);
const rowAction = ref('');
const isErrorForm = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const titleModal = ref('');
const tareaEditor = ref(null);
const currentTask = ref(null);

const formTarea = useForm({
    label: '',
    description: '',
    id_file: 0,
    description_collaborator: '',
    status: 0,
});

const fileExtension = computed(() => {
    if (!fileName.value) return '';
    return fileName.value.split('.').pop().toLowerCase();
});
const isPdfFile = computed(() => fileExtension.value === 'pdf');
const isImageFile = computed(() => ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(fileExtension.value));

const initModal = (id, action, data = {}) => {
    isErrorForm.value = false;
    isModalFullActive.value = false;
    modalFullSrc.value = '';
    existeAdjunto.value = false;
    filePath.value = '';
    fileName.value = '';
    rowAction.value = action;
    titleModal.value = action;
    currentTask.value = data;
    if (fileTaskResp.value) fileTaskResp.value.value = '';

    if (action === 'borrador') {
        titleModal.value = 'establecer como borrador';
    } else if (action === 'leer') {
        titleModal.value = 'Respuesta de la';
    } else if (action === 'leertarea') {
        titleModal.value = 'Vista de la';
    }

    if (action === 'registrar') {
        isModalActive.value = true;
    } else if (action === 'responder') {
        rowParentID.value = id;
        isModalActive.value = true;
        formTarea.label = 'RE: ' + (data['label'] || '');
    } else if (action === 'actualizar') {
        rowID.value = id;
        rowParentID.value = data['id_task'];
        isModalActive.value = true;
        formTarea.id_file = data['id_file'];
        if (data['id_file']) {
            existeAdjunto.value = true;
            filePath.value = data['filepath'];
            fileName.value = data['filename'];
        }
        formTarea.label = data['label'];
        formTarea.description = data['description'];
        formTarea.description_collaborator = data['description_collaborator'];
    } else if (action === 'leertarea') {
        rowParentID.value = id;
        isModalActive.value = true;
        formTarea.label = data['label'];
        formTarea.description = data['description'];
        if (data['id_file']) {
            existeAdjunto.value = true;
            filePath.value = data['filepath'];
            fileName.value = data['filename'];
        }
    } else if (action === 'leer') {
        rowID.value = id;
        isModalActive.value = true;
        formTarea.label = data['label'];
        formTarea.description = data['description'];
        formTarea.id_file = data['id_file'];
        if (data['id_file']) {
            existeAdjunto.value = true;
            filePath.value = data['filepath'];
            fileName.value = data['filename'];
        }
        formTarea.description_collaborator = data['description_collaborator'];
    } else {
        // Caso de confirmación de publicación
        rowID.value = id;
        isModalDangerActive.value = true;
    }
};

const isConfirm = () => {
    const ruta = 'tarearesponse-' + rowAction.value;

    if (rowAction.value === 'responder') {
        if (!formTarea.label) {
            isModalActive.value = true;
            isErrorForm.value = true;
        } else {
            axios.post(route(ruta), {
                id_task: rowParentID.value,
                label: formTarea.label,
                description: formTarea.description || ''
            }).then(async (response) => {
                const newRespId = response.data?.id || response.data?.taskResp?.id;
                if (newRespId && fileTaskResp.value?.files?.[0]) {
                    inputFileName.value = 'RESP' + newRespId + '_TAREA' + rowParentID.value;
                    await uploadFile('file_taskresp', newRespId);
                }
                cleanVal();
                isModalActive.value = false;
                Inertia.reload({ only: ['tasks', 'taskResponses'], preserveState: true });
            }).catch((err) => {
                console.error(err);
            });
        }
    } else if (rowAction.value === 'actualizar') {
        if (!formTarea.label) {
            isModalActive.value = true;
            isErrorForm.value = true;
        } else {
            axios.put(route(ruta), {
                id: rowID.value,
                label: formTarea.label,
                description: formTarea.description || ''
            }).then(async () => {
                if (fileTaskResp.value?.files?.[0]) {
                    inputFileName.value = 'RESP' + rowID.value + '_TAREA' + (rowParentID.value || '');
                    await uploadFile('file_taskresp', rowID.value);
                }
                cleanVal();
                isModalActive.value = false;
                Inertia.reload({ only: ['tasks', 'taskResponses'], preserveState: true });
            }).catch((err) => {
                console.error(err);
            });
        }
    } else {
        // Publicar
        axios.put(route(ruta), {
            id: rowID.value
        }).then(() => {
            cleanVal();
            Inertia.reload({ only: ['taskResponses'] });
        }).catch((err) => {
            console.error(err);
        });
    }
};

const isCancel = () => {
    cleanVal();
};

const refreshList = (action) => {
    Inertia.reload({ only: action });
};

const cleanVal = (cleanEditor = 1) => {
    rowID.value = 0;
    rowParentID.value = null;
    rowAction.value = '';
    modalFullSrc.value = '';
    existeAdjunto.value = false;
    filePath.value = '';
    fileName.value = '';
    errorMessage.value = '';
    successMessage.value = '';

    if (cleanEditor && tareaEditor.value?.setContents) {
        formTarea.id_file = null;
        formTarea.label = '';
        formTarea.description = '';
        formTarea.description_collaborator = '';
        formTarea.status = 0;
        tareaEditor.value.setContents('');
    }
    cleanFile(1);
};

// Adjuntos en tareas
const filePath = ref('');
const fileName = ref('');
const existeAdjunto = ref(false);
const isSaveFile = ref(false);
const ShowDeleteFile = ref(false);
const inputFileName = ref('');
const inputFileDesc = ref('');
const inputFilePos = ref(1);
const fileTaskResp = ref(null);
const fileSize = ref(0);

const formFile = useForm({
    id: 0,
    file: null,
    filename: '',
    src_object_type: 'file_taskresp',
    src_object_id: null,
    name: '',
    terms: false,
    type: ''
});

const cleanFile = (del_srcid = 0) => {
    formFile.id = null;
    formFile.file = null;
    formFile.filename = '';
    if (del_srcid) {
        formFile.src_object_id = null;
    }
    formFile.name = '';
    formFile.terms = false;
    formFile.type = '';
    existeAdjunto.value = false;
    isSaveFile.value = false;
    filePath.value = '';
    fileName.value = '';
    ShowDeleteFile.value = false;
};

const changeFile = (type) => {
    let file = null;
    if (type === 'file_taskresp') {
        file = fileTaskResp.value?.files?.[0];
    }
    if (file) {
        fileSize.value = file.size;
    }
};

const uploadFile = (type, origin_id = 0) => {
    const file = fileTaskResp.value?.files?.[0];
    if (!file) return Promise.resolve();

    const data = new FormData();
    data.append('file', file);
    data.append('origin_type', type);
    data.append('origin_id', origin_id);
    data.append('name', inputFileName.value || ('RESP' + origin_id + '_TAREA' + (rowParentID.value || '')));
    data.append('description', inputFileDesc.value);
    data.append('position', inputFilePos.value);

    return axios.post('/file/upload', data)
        .then(response => {
            if (response.data.error) {
                if (type === 'file_taskresp') {
                    errorMessage.value = response.data.message;
                }
            } else if (response.data.success) {
                successMessage.value = response.data.message;
                cleanInputFile();
                if (!props.debugInertia) {
                    Inertia.reload({ only: ['tasks', 'taskResponses'], preserveState: true });
                }
            }
            return response;
        })
        .catch(error => {
            console.error(error);
            throw error;
        });
};

const cleanInputFile = () => {
    inputFileName.value = '';
    inputFileDesc.value = '';
    inputFilePos.value = 1;
    fileSize.value = 0;
};

const deleteFile = (id_file, type = 'file_taskresp') => {
    const ruta = 'file-eliminar';
    axios.post(route(ruta), {
        id: id_file,
        src_object_type: type
    }).then((response) => {
        if (response.data.error) {
            errorMessage.value = response.data.message;
        } else if (response.data.success) {
            successMessage.value = response.data.message;
            if (type === 'file_resource') {
                rowID.value = 0;
                rowAction.value = '';
                Inertia.reload({ only: ['resources'] });
            } else if (type === 'file_taskresp') {
                isModalActive.value = false;
                isConfirm();
            }
        }
    }).catch((error) => {
        console.error(error);
    });
};

// Formatos de fecha y vencimiento inteligente
const formatDateTime = (datetime) => {
    if (!datetime) return '-';
    const date = new Date(datetime);
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    const hr = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${d}/${m}/${y} ${hr}:${min}`;
};

const formatDueDateBadge = (dateStr) => {
    if (!dateStr) return { text: 'Sin fecha límite', isUrgent: false, isOverdue: false };
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60));

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    const timeFormatted = `${hours}:${mins}`;

    if (diffHours < 0) {
        return { text: `Venció el ${day}/${month}`, full: `${day}/${month} ${timeFormatted}`, isOverdue: true, isUrgent: true };
    }
    if (diffHours <= 24) {
        return { text: `Hoy a las ${timeFormatted}`, full: `${day}/${month} ${timeFormatted}`, isUrgent: true, isToday: true };
    }
    if (diffHours <= 48) {
        return { text: `Mañana a las ${timeFormatted}`, full: `${day}/${month} ${timeFormatted}`, isUrgent: true };
    }
    return { text: `${day}/${month} ${timeFormatted}`, full: `${day}/${month} ${timeFormatted}`, isUrgent: false };
};

const getFileIcon = (ext = '') => {
    const e = (ext || '').toLowerCase();
    if (e.includes('pdf')) return mdiFilePdfBox;
    if (e.includes('doc')) return mdiFileWordBox;
    if (e.includes('xls')) return mdiFileExcelBox;
    return mdiFileDocumentOutline;
};

onMounted(() => {
    getPostit();
});
</script>

<template>
    <Head :title="object.label + ' - Aula ' + classroom_label" />

    <BreezeAuthenticatedLayout>
        <!-- Header con botón funcional para volver -->
        <template #header>
            <PremiumPageHeader
                :title="'AULA: ' + classroom_label + ' - CURSO: ' + object.label"
                :subtitle="'Clase digital y seguimiento académico del curso ' + object.label"
                icon="solar:book-bookmark-bold-duotone"
                color="blue"
            >
                <template #buttons>
                    <Link
                        :href="route('student-courses')"
                        class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/80 shadow-xs active:scale-95 transition-all"
                    >
                        <BaseIcon :path="mdiArrowLeft" :size="16" />
                        <span>Volver a mis cursos</span>
                    </Link>
                </template>
            </PremiumPageHeader>
        </template>

        <!-- Modales de la Clase -->
        <!-- 1. Modal de Tarea (Crear/Editar respuesta, ver detalles) -->
        <CardBoxModal
            v-model="isModalActive"
            hasCancel
            :title="titleModal + ' tarea'"
            @confirm="isConfirm"
            @cancel="isCancel"
            :sizeModal="existeAdjunto && (rowAction === 'leer' || rowAction === 'leertarea') ? 'large' : 'medium'"
            :noButtons="rowAction === 'leer' || rowAction === 'leertarea'"
        >
            <div v-if="isErrorForm" class="mb-4 p-3 rounded-xl bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 text-xs font-semibold">
                ¡Atención! Debe registrar los campos requeridos para enviar la tarea.
            </div>

            <!-- Banner Informativo para Tareas Físicas o Formativas -->
            <div v-if="currentTask?.delivery_type === 'fisico'" class="mb-4 p-3.5 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900/40 flex items-start gap-2.5 text-xs text-purple-900 dark:text-purple-200">
                <BaseIcon :path="mdiBook" :size="18" class="text-purple-600 shrink-0 mt-0.5" />
                <div>
                    <strong class="font-bold">Recepción Física en Aula:</strong>
                    <span class="ml-1">Esta tarea se revisará directamente en tu cuaderno u hojas de trabajo en clase. Puedes adjuntar fotos si tu docente lo requiere, pero la revisión principal es presencial.</span>
                </div>
            </div>

            <div v-if="currentTask?.is_graded == 0" class="mb-4 p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 text-xs text-amber-800 dark:text-amber-300 font-medium flex items-center gap-2">
                <BaseIcon :path="mdiInformationOutline" :size="16" class="text-amber-600 shrink-0" />
                <span>Esta tarea es de tipo <b>Formativa</b> (sin nota numérica oficial).</span>
            </div>

            <div class="space-y-4">
                <div>
                    <BreezeLabel for="label" value="Título de la respuesta" />
                    <BreezeInput
                        id="label"
                        type="text"
                        class="mt-1 block w-full rounded-xl"
                        v-model="formTarea.label"
                        :disabled="rowAction === 'leer' || rowAction === 'leertarea'"
                        required
                    />
                </div>

                <div>
                    <BreezeLabel for="description" value="Contenido / Desarrollo" />
                    <div class="mt-1 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                        <QuillEditor
                            v-if="rowAction === 'leer' || rowAction === 'leertarea'"
                            ref="tareaEditor"
                            style="min-height: 140px;"
                            v-model:content="formTarea.description"
                            contentType="html"
                            :toolbar="[]"
                            theme="snow"
                            readOnly
                        />
                        <QuillEditor
                            v-else
                            ref="tareaEditor"
                            style="min-height: 140px;"
                            v-model:content="formTarea.description"
                            contentType="html"
                            toolbar="essential"
                            theme="snow"
                            placeholder="Escriba aquí el desarrollo de su tarea..."
                        />
                    </div>
                </div>

                <div v-if="rowAction === 'leer' && formTarea.description_collaborator">
                    <BreezeLabel for="comment" value="Comentario / Retroalimentación del docente" />
                    <div class="mt-1 p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40 text-xs text-blue-900 dark:text-blue-200">
                        <div v-html="formTarea.description_collaborator"></div>
                    </div>
                </div>

                <!-- 1. Adjuntar archivo al momento de responder (Imagen 4) -->
                <div v-if="rowAction === 'responder'" class="p-3.5 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2">
                    <div class="flex items-center gap-2">
                        <BaseIcon :path="mdiPaperclip" :size="16" class="text-blue-600" />
                        <span class="text-xs font-bold text-slate-700 dark:text-slate-300">Adjuntar archivo a su respuesta (opcional)</span>
                    </div>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400">Puede adjuntar su archivo (PDF, fotos de su cuaderno o documento) directamente ahora al responder.</p>
                    <div v-if="errorMessage" class="text-xs text-red-600 font-medium">{{ errorMessage }}</div>
                    <input
                        id="file_taskresp_initial"
                        ref="fileTaskResp"
                        type="file"
                        class="block w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        @change="changeFile('file_taskresp')"
                        accept=".PDF, .PNG, .JPG, .JPEG, .WEBP, .DOC, .DOCX"
                    />
                </div>

                <!-- Subir o Administrar Adjunto al editar respuesta -->
                <div v-if="rowAction === 'actualizar'" class="p-3 bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-800 space-y-3">
                    <template v-if="!existeAdjunto">
                        <p class="text-xs font-semibold text-gray-700 dark:text-gray-300">Subir archivo adjunto (opcional)</p>
                        <div v-if="errorMessage" class="text-xs text-red-600 font-medium">{{ errorMessage }}</div>
                        <input
                            id="file_taskresp"
                            ref="fileTaskResp"
                            type="file"
                            class="block w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            @change="changeFile('file_taskresp')"
                            accept=".PDF, .PNG, .JPG, .JPEG, .WEBP, .DOC, .DOCX"
                        />
                        <BreezeButton
                            type="button"
                            class="!py-1.5 !px-3 !text-xs !rounded-lg"
                            @click="uploadFile('file_taskresp', rowID)"
                            :set="inputFileName = 'RESP' + rowID + '_TAREA' + rowParentID"
                        >
                            Guardar Adjunto
                        </BreezeButton>
                    </template>
                    <template v-else>
                        <div class="flex items-center justify-between gap-2">
                            <div class="flex items-center gap-2 truncate text-xs text-gray-700 dark:text-gray-300">
                                <BaseIcon :path="mdiPaperclip" :size="16" class="text-blue-600 shrink-0" />
                                <span class="truncate font-medium">{{ fileName }}</span>
                            </div>
                            <div class="flex items-center gap-2 shrink-0">
                                <BaseButton
                                    color="info"
                                    :icon="mdiDownload"
                                    small
                                    title="Descargar"
                                    :href="route('file-download', { 'srctype': 'file_taskresp', 'srcid': rowID, 'filename': fileName })"
                                    target="_blank"
                                />
                                <button
                                    type="button"
                                    @click="deleteFile(formTarea.id_file)"
                                    class="text-xs text-red-600 hover:text-red-700 font-semibold underline"
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- 2. Vista Previa Integrada de la Tarea / Material Adjunto (Imagen 3) -->
                <div v-else-if="(rowAction === 'leer' || rowAction === 'leertarea') && existeAdjunto && filePath && fileName" class="space-y-3 pt-2">
                    <div class="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700">
                        <div class="flex items-center gap-2 min-w-0">
                            <BaseIcon :path="mdiPaperclip" :size="16" class="text-blue-600 shrink-0" />
                            <span class="truncate text-xs font-bold text-slate-800 dark:text-slate-200">{{ fileName }}</span>
                            <span 
                                :class="[
                                    'px-2 py-0.5 rounded text-[10px] uppercase font-black shrink-0',
                                    isPdfFile ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300' : (isImageFile ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' : 'bg-slate-200 text-slate-700')
                                ]"
                            >
                                {{ fileExtension }}
                            </span>
                        </div>
                        <div class="flex items-center gap-2 shrink-0">
                            <button
                                v-if="isPdfFile || isImageFile"
                                type="button"
                                @click="(isModalFullActive = true, modalFullSrc = '/documents/' + filePath + '/' + fileName)"
                                class="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 shadow-2xs transition"
                            >
                                <BaseIcon :path="mdiEye" :size="14" />
                                <span>Pantalla completa</span>
                            </button>
                            <BaseButton
                                color="info"
                                :icon="mdiDownload"
                                small
                                label="Descargar"
                                :href="route('file-download', { 'srctype': (rowAction === 'leertarea' ? 'file_task' : 'file_taskresp'), 'srcid': (rowAction === 'leertarea' ? rowParentID : rowID), 'filename': fileName })"
                                target="_blank"
                            />
                        </div>
                    </div>

                    <!-- Vista previa embebida para PDF -->
                    <div v-if="isPdfFile" class="w-full rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-inner bg-slate-100 dark:bg-slate-950">
                        <embed :src="'/documents/' + filePath + '/' + fileName + '#toolbar=0'" class="w-full h-[320px] sm:h-[400px]" type="application/pdf" />
                    </div>

                    <!-- Vista previa embebida para Imágenes con Scroll interno -->
                    <div v-else-if="isImageFile" class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/60 overflow-hidden">
                        <div class="max-h-[280px] sm:max-h-[330px] overflow-y-auto p-3 flex justify-center scrollbar-thin">
                            <img 
                                :src="'/documents/' + filePath + '/' + fileName" 
                                :alt="fileName" 
                                class="max-w-full h-auto rounded-xl object-contain shadow-xs cursor-pointer hover:opacity-95 transition" 
                                @click="(isModalFullActive = true, modalFullSrc = '/documents/' + filePath + '/' + fileName)"
                                title="Click para ver en pantalla completa"
                            />
                        </div>
                        <div class="px-3 py-1.5 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200/60 dark:border-slate-700/60 text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
                            <span class="flex items-center gap-1">
                                <BaseIcon :path="mdiInformationOutline" :size="13" class="text-blue-500" />
                                <span>Desplaza verticalmente para ver todo el documento</span>
                            </span>
                            <button 
                                type="button" 
                                @click="(isModalFullActive = true, modalFullSrc = '/documents/' + filePath + '/' + fileName)"
                                class="text-blue-600 dark:text-blue-400 font-bold hover:underline flex items-center gap-1"
                            >
                                <BaseIcon :path="mdiEye" :size="13" />
                                <span>Pantalla completa</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </CardBoxModal>

        <!-- 2. Modal de Vista Previa de Documentos a Pantalla Completa (Lightbox con mayor zIndex) -->
        <CardBoxModal v-model="isModalFullActive" title="Vista previa de documento" sizeModal="full" zIndex="z-[80]" hasCancel>
            <div class="flex justify-center items-center w-full max-h-[82vh] overflow-auto p-2 bg-slate-50 dark:bg-slate-950 rounded-xl">
                <img 
                    v-if="modalFullSrc && ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(modalFullSrc.split('.').pop().toLowerCase())" 
                    :src="modalFullSrc" 
                    class="max-h-[78vh] w-auto max-w-full rounded-xl object-contain shadow-md" 
                    alt="Vista previa completa" 
                />
                <embed 
                    v-else-if="modalFullSrc" 
                    :src="modalFullSrc" 
                    class="min-h-[75vh] max-h-[80vh] w-full rounded-xl border border-gray-200 dark:border-gray-700" 
                />
            </div>
        </CardBoxModal>

        <!-- 3. Modal de Confirmación para Publicar Tarea -->
        <CardBoxModal
            v-model="isModalDangerActive"
            title="Confirmar Entrega Definitiva"
            button="danger"
            hasCancel
            @confirm="isConfirm"
            @cancel="isCancel"
        >
            <div class="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <p>¿Está seguro de <b>{{ titleModal }}</b>?</p>
                <p class="text-xs text-amber-600 dark:text-amber-400 font-semibold">
                    ¡Una vez enviada la tarea al docente ya no podrá modificarla, a menos que sea devuelta a borrador!
                </p>
            </div>
        </CardBoxModal>

        <!-- Estructura Principal Multidispositivo (9 cols Contenido + 3 cols Sidebar en Desktop) -->
        <div class="space-y-6 pb-12">
            <!-- Barra de Pestañas Moderna con Scroll Horizontal para Móviles -->
            <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-1.5 sm:p-2">
                <div class="flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-none">
                    <button
                        type="button"
                        @click="toggleTabs(1)"
                        :class="[
                            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-150',
                            openTab === 1
                                ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/25'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        ]"
                    >
                        <BaseIcon :path="mdiBookOpenVariant" :size="18" />
                        <span>Contenido del curso</span>
                    </button>

                    <button
                        type="button"
                        @click="toggleTabs(2)"
                        :class="[
                            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-150',
                            openTab === 2
                                ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/25'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        ]"
                    >
                        <BaseIcon :path="mdiClockOutline" :size="18" />
                        <span>Tareas</span>
                        <span
                            v-if="pendingTasksCount > 0"
                            class="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white"
                        >
                            {{ pendingTasksCount }}
                        </span>
                        <span
                            v-else-if="tasks.length"
                            class="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                        >
                            {{ tasks.length }}
                        </span>
                    </button>

                    <button
                        type="button"
                        @click="toggleTabs(3)"
                        :class="[
                            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-150',
                            openTab === 3
                                ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/25'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        ]"
                    >
                        <BaseIcon :path="mdiFileDocumentOutline" :size="18" />
                        <span>Recursos de estudio</span>
                        <span
                            v-if="totalResourcesCount > 0"
                            class="px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                        >
                            {{ totalResourcesCount }}
                        </span>
                    </button>

                    <button
                        type="button"
                        @click="toggleTabs(4)"
                        :class="[
                            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-150',
                            openTab === 4
                                ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/25'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        ]"
                    >
                        <BaseIcon :path="mdiStarOutline" :size="18" />
                        <span>Mis calificaciones</span>
                    </button>
                </div>
            </div>

            <!-- Grid Principal: En móvil el contenido aparece PRIMERO, la barra lateral DEBAJO -->
            <div class="grid grid-cols-1 xl:grid-cols-12 gap-6">
                <!-- Área de Contenido Académico (Colspan 8 o 9 en desktop) -->
                <div class="col-span-1 xl:col-span-8 2xl:col-span-9 space-y-6 order-1">
                    <!-- ================= TAB 1: CONTENIDO DEL CURSO ================= -->
                    <div v-show="openTab === 1" class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 sm:p-6">
                        <ClassroomTopicManager
                            :idClassroom="classroom ? classroom.id : 0"
                            :idCourse="object.id"
                            :ciclos="ciclos"
                            :canWrite="false"
                            :refCicloActual="refCicloActual"
                            :studentId="studentId"
                            :courseName="object.label"
                            :classroomName="classroom_label"
                            :classroomYear="classroom ? classroom.year : ''"
                            :semanaActual="semanaActual"
                        />
                    </div>

                    <!-- ================= TAB 2: TAREAS ================= -->
                    <div v-show="openTab === 2" class="space-y-4">
                        <!-- Toolbar y Mensaje Guía -->
                        <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div class="flex items-center gap-2">
                                <BaseButton
                                    :icon="mdiRefresh"
                                    color="info"
                                    icon-size="18"
                                    small
                                    label="Actualizar tareas"
                                    @click="refreshList(['tasks', 'taskResponses'])"
                                />
                            </div>
                            <p class="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                <BaseIcon :path="mdiInformationOutline" :size="15" class="text-blue-500 shrink-0" />
                                <span>Cree una respuesta para iniciar. Al publicar, su tarea será enviada al docente.</span>
                            </p>
                        </div>

                        <!-- Lista de Tareas o Empty State -->
                        <div v-if="tasks.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-10 text-center">
                            <div class="w-14 h-14 mx-auto mb-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                <BaseIcon :path="mdiCheckCircle" :size="28" />
                            </div>
                            <h4 class="text-base font-bold text-gray-800 dark:text-white">Sin tareas pendientes</h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Aún no se han asignado tareas en esta materia.</p>
                        </div>

                        <div v-else class="space-y-3">
                            <!-- Vista Tarjetas de Tareas (Excelente para móvil, tablet y PC) -->
                            <div
                                v-for="task in tasks"
                                :key="task.id"
                                class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:shadow-md transition-all"
                            >
                                <!-- Info Principal -->
                                <div class="space-y-1.5 flex-1 min-w-0">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <!-- Modalidad Física vs Digital -->
                                        <span
                                            v-if="task.delivery_type === 'fisico'"
                                            class="inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-bold rounded-full bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200/60"
                                        >
                                            <BaseIcon :path="mdiBook" :size="12" />
                                            Recepción Física (Cuaderno)
                                        </span>

                                        <!-- Pill de Estado para tareas digitales -->
                                        <span
                                            v-else-if="!taskResponsesMap[task.id]"
                                            class="inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-bold rounded-full bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                                        >
                                            <BaseIcon :path="mdiClockOutline" :size="12" />
                                            Sin responder
                                        </span>
                                        <span
                                            v-if="taskResponsesMap[task.id] && !taskResponsesMap[task.id].status"
                                            class="inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-bold rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                                        >
                                            <BaseIcon :path="mdiPencil" :size="12" />
                                            Borrador guardado
                                            <BaseIcon v-if="taskResponsesMap[task.id].id_file" :path="mdiPaperclip" :size="12" />
                                        </span>
                                        <span
                                            v-else-if="taskResponsesMap[task.id] && taskResponsesMap[task.id].status"
                                            class="inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-bold rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                                        >
                                            <BaseIcon :path="mdiCheckCircle" :size="12" />
                                            Entregado / Publicado
                                            <BaseIcon v-if="taskResponsesMap[task.id].id_file" :path="mdiPaperclip" :size="12" />
                                        </span>

                                        <!-- Badge Formativa o Calificación -->
                                        <span
                                            v-if="task.is_graded == 0"
                                            class="px-2 py-0.5 text-[11px] font-bold rounded-lg bg-amber-50 text-amber-700 border border-amber-200"
                                        >
                                            Formativa (Sin nota)
                                        </span>
                                        <span
                                            v-else-if="taskResponsesMap[task.id]?.calification !== null && taskResponsesMap[task.id]?.calification !== undefined"
                                            :class="[
                                                'px-2 py-0.5 text-[11px] font-extrabold rounded-lg',
                                                parseInt(taskResponsesMap[task.id].calification) >= 11
                                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
                                                    : 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200'
                                            ]"
                                        >
                                            Nota: {{ parseInt(taskResponsesMap[task.id].calification) }}
                                        </span>
                                    </div>

                                    <h4 class="text-base font-bold text-gray-900 dark:text-white leading-snug">
                                        {{ task.label }}
                                    </h4>

                                    <!-- Fechas de Vencimiento y Entrega -->
                                    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 pt-1">
                                        <div class="flex items-center gap-1">
                                            <BaseIcon :path="mdiCalendarCheck" :size="14" />
                                            <span>Vence:</span>
                                            <strong
                                                :class="[
                                                    formatDueDateBadge(task.end_date).isUrgent
                                                        ? 'text-rose-600 dark:text-rose-400 font-bold'
                                                        : 'text-gray-700 dark:text-gray-300'
                                                ]"
                                            >
                                                {{ formatDueDateBadge(task.end_date).text }}
                                            </strong>
                                        </div>

                                        <div v-if="taskResponsesMap[task.id]?.status" class="flex items-center gap-1 text-gray-400">
                                            <span>Enviado: {{ formatDateTime(taskResponsesMap[task.id].end_date) }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Botones de Acción Táctiles y Accesibles -->
                                <div class="flex flex-wrap items-center gap-2 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-gray-100 dark:border-gray-700/60">
                                    <!-- Ver tarea -->
                                    <button
                                        type="button"
                                        @click="initModal(task.id, 'leertarea', task)"
                                        class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                        title="Ver detalles de la tarea"
                                    >
                                        <BaseIcon :path="mdiEye" :size="15" />
                                        <span>Detalles</span>
                                    </button>

                                    <!-- Responder si aún no hay respuesta -->
                                    <button
                                        v-if="!taskResponsesMap[task.id]"
                                        type="button"
                                        @click="initModal(task.id, 'responder', task)"
                                        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-500/20 active:scale-95 transition-all"
                                    >
                                        <BaseIcon :path="mdiPlusCircle" :size="15" />
                                        <span>Responder</span>
                                    </button>

                                    <!-- Si ya tiene respuesta -->
                                    <template v-else>
                                        <button
                                            v-if="taskResponsesMap[task.id].status"
                                            type="button"
                                            @click="initModal(taskResponsesMap[task.id].id, 'leer', taskResponsesMap[task.id])"
                                            class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 transition-colors"
                                        >
                                            <BaseIcon :path="mdiEye" :size="15" />
                                            <span>Mi Entrega</span>
                                        </button>

                                        <template v-else>
                                            <button
                                                type="button"
                                                @click="initModal(taskResponsesMap[task.id].id, 'actualizar', taskResponsesMap[task.id])"
                                                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 transition-colors"
                                            >
                                                <BaseIcon :path="mdiPencil" :size="15" />
                                                <span>Editar</span>
                                            </button>
                                            <button
                                                type="button"
                                                @click="initModal(taskResponsesMap[task.id].id, 'publicar')"
                                                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm shadow-emerald-500/20 active:scale-95 transition-all"
                                            >
                                                <BaseIcon :path="mdiSend" :size="15" />
                                                <span>Entregar</span>
                                            </button>
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ================= TAB 3: RECURSOS DE ESTUDIO ================= -->
                    <div v-show="openTab === 3" class="space-y-6">
                        <div class="flex items-center justify-between">
                            <h4 class="text-sm font-bold text-gray-700 dark:text-gray-300">Material de apoyo académico</h4>
                            <BaseButton :icon="mdiRefresh" color="info" icon-size="16" small label="Refrescar" @click="refreshList(['resources', 'resourceLinks'])" />
                        </div>

                        <!-- 1. Documentos y Archivos -->
                        <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-5 space-y-4">
                            <div class="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
                                <BaseIcon :path="mdiFileDocumentOutline" :size="20" class="text-blue-600" />
                                <h5>Documentos del Docente</h5>
                                <span class="text-xs font-semibold text-gray-400">({{ resources?.length || 0 }})</span>
                            </div>

                            <div v-if="resources.length" class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div
                                    v-for="resource in resources"
                                    :key="resource.id"
                                    class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 hover:shadow-xs transition-all"
                                >
                                    <div class="flex items-center gap-3 min-w-0">
                                        <div class="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 flex items-center justify-center shrink-0">
                                            <BaseIcon :path="getFileIcon(resource.extension)" :size="22" />
                                        </div>
                                        <div class="min-w-0">
                                            <h6 class="text-xs font-bold text-gray-900 dark:text-white truncate" :title="resource.name">
                                                {{ resource.name }}
                                            </h6>
                                            <p v-if="resource.description" class="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                                {{ resource.description }}
                                            </p>
                                        </div>
                                    </div>

                                    <div class="flex items-center gap-1 shrink-0">
                                        <button
                                            type="button"
                                            @click="(isModalFullActive = true, modalFullSrc = '/documents/' + resource.filepath + '/' + resource.filename)"
                                            class="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                                            title="Ver archivo"
                                        >
                                            <BaseIcon :path="mdiEye" :size="18" />
                                        </button>
                                        <a
                                            :href="route('file-download2', { 'srctype': 'file_resource', 'srcid': resource.src_object_id, 'filename': resource.filename, 'srcid2': resource.origin_id2 })"
                                            target="_blank"
                                            class="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                                            title="Descargar archivo"
                                        >
                                            <BaseIcon :path="mdiDownload" :size="18" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="text-xs text-gray-400 italic">No hay archivos adjuntos en esta clase.</p>
                        </div>

                        <!-- 2. Enlaces de Interés -->
                        <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-5 space-y-4">
                            <div class="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
                                <BaseIcon :path="mdiLinkVariant" :size="20" class="text-emerald-600" />
                                <h5>Enlaces recomendados</h5>
                                <span class="text-xs font-semibold text-gray-400">({{ resourceLinks?.length || 0 }})</span>
                            </div>

                            <div v-if="resourceLinks.length" class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <a
                                    v-for="link in resourceLinks"
                                    :key="link.id"
                                    :href="link.url"
                                    target="_blank"
                                    class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 hover:border-blue-200 dark:hover:border-blue-800/60 transition-all group"
                                >
                                    <div class="min-w-0">
                                        <h6 class="text-xs font-bold text-gray-900 dark:text-white truncate group-hover:text-blue-600 transition-colors">
                                            {{ link.label }}
                                        </h6>
                                        <p v-if="link.description" class="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                            {{ link.description }}
                                        </p>
                                    </div>
                                    <BaseIcon :path="mdiLinkVariant" :size="18" class="text-gray-400 group-hover:text-blue-600 shrink-0" />
                                </a>
                            </div>
                            <p v-else class="text-xs text-gray-400 italic">No hay enlaces web registrados.</p>
                        </div>
                    </div>

                    <!-- ================= TAB 4: MIS CALIFICACIONES ================= -->
                    <div v-show="openTab === 4" class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-5 space-y-4">
                        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700/60 pb-3">
                            <div>
                                <h4 class="text-base font-bold text-gray-900 dark:text-white">Rendimiento y Calificaciones</h4>
                                <p class="text-xs text-gray-500 dark:text-gray-400">Registro oficial de evaluaciones por periodo escolar</p>
                            </div>
                            <span class="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800">
                                {{ activeCriteria.length >= 9 ? 'Todos los Criterios y Exámenes' : (activeCriteria.length === 3 && showExamenParcial && showExamenFinal ? 'Solo Exámenes' : `${activeCriteria.length} Criterios Visibles`) }}
                            </span>
                        </div>

                        <div v-if="arrayCalifications && arrayCalifications.length" class="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700/60">
                            <table class="w-full text-left text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                                <thead class="bg-gray-50 dark:bg-gray-900/50 uppercase text-[11px] font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/60">
                                    <tr>
                                        <th class="py-3 px-3 text-left">Periodo</th>
                                        <th
                                            v-for="crit in visibleEvalCriteria"
                                            :key="crit.key"
                                            class="py-3 px-2 text-center"
                                            :title="crit.label"
                                        >
                                            {{ crit.label }}
                                        </th>
                                        <th v-if="showExamenParcial" class="py-3 px-2 text-center">Ex. Parcial</th>
                                        <th v-if="showExamenParcial2" class="py-3 px-2 text-center">Ex. Parcial 2</th>
                                        <th v-if="showExamenFinal" class="py-3 px-2 text-center">Ex. Final</th>
                                        <th v-if="confGlobal.STUDENT_CALIFICATION_SHOW_PROMEDIO == '1'" class="py-3 px-3 text-center">Promedio</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
                                    <tr v-for="cal in arrayCalifications" :key="cal.id" class="hover:bg-gray-50/50 dark:hover:bg-gray-700/30">
                                        <td class="py-3.5 px-3 font-bold text-gray-900 dark:text-white">
                                            {{ cal.ciclo_label || cal.label }}
                                        </td>
                                        <td
                                            v-for="crit in visibleEvalCriteria"
                                            :key="crit.key"
                                            class="py-3.5 px-2 text-center font-medium"
                                        >
                                            <span :class="cal[crit.key] !== null && cal[crit.key] !== undefined && Number(cal[crit.key]) < 11 ? 'text-rose-600 font-bold' : ''">
                                                {{ cal[crit.key] ?? '-' }}
                                            </span>
                                        </td>
                                        <td v-if="showExamenParcial" class="py-3.5 px-2 text-center font-semibold">
                                            <span :class="cal.examen_parcial !== null && cal.examen_parcial !== undefined && Number(cal.examen_parcial) < 11 ? 'text-rose-600 font-bold' : 'text-blue-600'">
                                                {{ cal.examen_parcial ?? '-' }}
                                            </span>
                                        </td>
                                        <td v-if="showExamenParcial2" class="py-3.5 px-2 text-center font-semibold">
                                            <span :class="cal.examen_parcial2 !== null && cal.examen_parcial2 !== undefined && Number(cal.examen_parcial2) < 11 ? 'text-rose-600 font-bold' : 'text-blue-600'">
                                                {{ cal.examen_parcial2 ?? '-' }}
                                            </span>
                                        </td>
                                        <td v-if="showExamenFinal" class="py-3.5 px-2 text-center font-semibold">
                                            <span :class="cal.examen_final !== null && cal.examen_final !== undefined && Number(cal.examen_final) < 11 ? 'text-rose-600 font-bold' : 'text-blue-600'">
                                                {{ cal.examen_final ?? '-' }}
                                            </span>
                                        </td>
                                        <td v-if="confGlobal.STUDENT_CALIFICATION_SHOW_PROMEDIO == '1'" class="py-3.5 px-3 text-center">
                                            <span
                                                :class="[
                                                    'px-2.5 py-1 rounded-lg text-xs font-black inline-block',
                                                    cal.promedio >= 11
                                                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                                                        : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
                                                ]"
                                            >
                                                {{ cal.promedio ?? '-' }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-else class="p-8 text-center bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                            <BaseIcon :path="mdiInformationOutline" :size="24" class="text-amber-500 mx-auto mb-2" />
                            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">Calificaciones aún no publicadas</p>
                            <p class="text-xs text-gray-400 mt-0.5">El docente aún no ha liberado las notas de los periodos evaluados.</p>
                        </div>
                    </div>
                </div>

                <!-- Barra Lateral de la Clase (Colspan 4 o 3 en desktop, orden 2 en móvil) -->
                <div class="col-span-1 xl:col-span-4 2xl:col-span-3 space-y-5 order-2">
                    <!-- 1. Widget de Calendario y Estado de la Sesión -->
                    <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm overflow-hidden">
                        <div class="bg-gradient-to-r from-rose-600 to-pink-600 p-3.5 text-center text-white">
                            <p class="text-xs font-extrabold uppercase tracking-widest">{{ nowMonthLetter }}</p>
                        </div>
                        <div class="p-4 text-center space-y-1">
                            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{{ nowDayLetter }}</p>
                            <h3 class="text-4xl font-black text-gray-900 dark:text-white leading-none">
                                {{ nowDay }}
                            </h3>
                            <div class="pt-2 flex items-center justify-center gap-2">
                                <span class="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                    Semana {{ semanaActual }}
                                </span>
                                <span class="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                    {{ bimestreActual }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- 2. Widget de Anuncios del Docente -->
                    <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 space-y-3">
                        <div class="flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/60 pb-2.5">
                            <div class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 flex items-center justify-center shrink-0">
                                <BaseIcon :path="mdiBullhornOutline" :size="18" />
                            </div>
                            <h5 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Anuncios de clase</h5>
                        </div>

                        <div class="overflow-y-auto max-h-52 space-y-2 pr-1 scrollbar-thin">
                            <template v-if="anuncios.length">
                                <div
                                    v-for="anuncio in anuncios"
                                    :key="anuncio.id"
                                    :class="[
                                        'p-2.5 rounded-xl border text-xs font-medium transition-all',
                                        anuncio.status == 1
                                            ? 'bg-emerald-50/60 text-emerald-900 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-300 dark:border-emerald-900/40'
                                            : 'bg-gray-50 text-gray-600 border-gray-100 dark:bg-gray-900/40 dark:text-gray-400 dark:border-gray-800'
                                    ]"
                                >
                                    {{ anuncio.label }}
                                </div>
                            </template>
                            <p v-else class="text-xs text-gray-400 italic py-2 text-center">
                                Sin anuncios recientes del docente.
                            </p>
                        </div>
                    </div>

                    <!-- 3. Widget de Bloc de Notas / Post-it del Estudiante -->
                    <div class="bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-200 dark:border-amber-900/40 shadow-sm p-4 space-y-2.5">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <img src="/img/pin.png" class="h-5 w-auto" alt="Pin" />
                                <span class="text-xs font-bold text-amber-900 dark:text-amber-200">Mis notas privadas</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span v-if="alertPostit" class="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 animate-pulse">
                                    {{ alertPostit }}
                                </span>
                                <span v-else-if="isSavingPostit" class="text-[11px] font-medium text-amber-600 dark:text-amber-400 animate-pulse">
                                    Guardando...
                                </span>
                                <button
                                    type="button"
                                    @click="updateOrCreatePostit(postit.id, postit.description)"
                                    :disabled="isSavingPostit"
                                    class="p-1.5 rounded-lg bg-amber-200/80 hover:bg-amber-300 text-amber-900 dark:bg-amber-900/50 dark:hover:bg-amber-800 dark:text-amber-100 transition-colors shadow-2xs disabled:opacity-50"
                                    title="Guardar nota"
                                >
                                    <BaseIcon :path="mdiContentSave" :size="16" />
                                </button>
                            </div>
                        </div>

                        <textarea
                            ref="nota_privada_label"
                            v-model="postit.description"
                            @blur="updateOrCreatePostit(postit.id, postit.description)"
                            rows="5"
                            placeholder="Escribe apuntes o recordatorios de esta clase aquí..."
                            class="w-full text-xs text-amber-950 dark:text-amber-100 bg-transparent border-none focus:ring-0 p-1 resize-none placeholder-amber-400 dark:placeholder-amber-600/70"
                        ></textarea>
                    </div>
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>